// App-wide service status: is the API there, is a maintenance window
// announced, and has a new build shipped under us.
//
// Built 2026-08-07. The problem it solves (Chauncey): a prod deploy restarts
// the API and blacks out the portal app for several minutes, and to anyone
// mid-task the app "just appears as if it's crashed."
//
// Three signals, one poll:
//   notice  — written by the ADO pipeline BEFORE it takes prod down. This is
//             why the announcement can't be computed client-side: during the
//             outage nothing can serve it, so clients must have learned about
//             it while everything was still healthy.
//   reach   — the poll succeeding or failing IS the liveness check. Only
//             transport-level failures count (ApiError 0 / 502 / 503 / 504);
//             a 401/403 means the API answered fine and the problem is auth,
//             which is not an outage and must not raise a scary banner.
//   build   — the API's build stamp. A change means new code is live and this
//             tab is running the old bundle.

import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError, API_BASE, type ServiceNotice } from '@/api/client'
import { useApiToken } from '@/auth/useApiToken'

const POLL_OK_MS = 60_000 // healthy cadence
const POLL_DOWN_MS = 5_000 // while unreachable, check back quickly
const PROBE_TIMEOUT_MS = 8_000
// One failure is a blip (a single dropped request, a token refresh racing a
// restart). Two consecutive failures is an outage worth telling someone about.
const FAILURES_BEFORE_DOWN = 2

export type Reach = 'ok' | 'down'

export interface ServiceStatus {
  reach: Reach
  notice: ServiceNotice | null
  /** A different build is live — this tab is stale and should reload. */
  newBuild: boolean
  /** True once we've had at least one successful poll (avoids a flash). */
  ready: boolean
}

function isOutage(err: unknown): boolean {
  if (err instanceof ApiError) return err.status === 0 || err.status >= 502
  return true // AbortError / anything non-HTTP is a transport failure
}

export function useServiceStatus(): ServiceStatus {
  const getToken = useApiToken()
  const [reach, setReach] = useState<Reach>('ok')
  const [notice, setNotice] = useState<ServiceNotice | null>(null)
  const [newBuild, setNewBuild] = useState(false)
  const [ready, setReady] = useState(false)

  const failures = useRef(0)
  const firstBuild = useRef<string | null>(null)
  const stopped = useRef(false)

  // Cheap anonymous liveness used only while we believe the API is down.
  // /health is served by the boot shim before the app finishes loading, so it
  // turns 200 the moment the new container can answer at all — the earliest
  // honest "it's coming back" signal there is.
  const probeHealth = useCallback(async (): Promise<boolean> => {
    const ctl = new AbortController()
    const t = setTimeout(() => ctl.abort(), PROBE_TIMEOUT_MS)
    try {
      const res = await fetch(`${API_BASE.replace(/\/api$/, '')}/health`, { signal: ctl.signal })
      return res.ok
    } catch {
      return false
    } finally {
      clearTimeout(t)
    }
  }, [])

  useEffect(() => {
    stopped.current = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const schedule = (ms: number) => {
      if (stopped.current) return
      timer = setTimeout(() => { void tick() }, ms)
    }

    const tick = async (): Promise<void> => {
      if (stopped.current) return
      // Don't poll a backgrounded tab — it wakes on visibilitychange below.
      if (document.visibilityState === 'hidden') { schedule(POLL_OK_MS); return }

      try {
        const token = await getToken()
        const res = await api.notice(token)
        if (stopped.current) return
        failures.current = 0
        setReach('ok')
        setReady(true)
        setNotice(res.notice)
        if (firstBuild.current === null) {
          firstBuild.current = res.build
        } else if (res.build !== firstBuild.current && res.build !== 'unknown') {
          setNewBuild(true)
        }
        schedule(POLL_OK_MS)
      } catch (err) {
        if (stopped.current) return
        if (!isOutage(err)) {
          // The API answered — auth or permission trouble, not an outage.
          // Stay quiet and try again on the normal cadence.
          schedule(POLL_OK_MS)
          return
        }
        failures.current += 1
        if (failures.current >= FAILURES_BEFORE_DOWN) {
          setReach('down')
          // While down, fall back to the anonymous probe so recovery is
          // detected even if token acquisition is also struggling.
          const back = await probeHealth()
          if (stopped.current) return
          if (back) { failures.current = 0; setReach('ok'); schedule(0); return }
        }
        schedule(POLL_DOWN_MS)
      }
    }

    void tick()
    const onVisible = () => {
      if (document.visibilityState === 'visible') { clearTimeout(timer); void tick() }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      stopped.current = true
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [getToken, probeHealth])

  return { reach, notice, newBuild, ready }
}
